import { useState, useEffect, useMemo, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useUserRole } from "@/hooks/useUserRole";
import { supabase } from "@/integrations/supabase/client";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { 
  Folder, Plus, Trash2, FileText, Lock, Archive, ArchiveRestore, 
  Search, Edit2, Copy, Download, Check, ArrowUpDown, Brain, Sparkles,
  ArrowRight, Upload, Clock
} from "lucide-react";
import { Link } from "react-router-dom";
import { Math } from "@/components/Math";
import { useTranslation } from "react-i18next";

interface LibraryGroup {
  id: string;
  name: string;
  created_at: string;
  archived: boolean;
}

interface LibraryItem {
  id: string;
  group_id: string | null;
  result_type: string;
  query: string;
  solution: string;
  created_at: string;
  updated_at: string;
  archived: boolean;
}

type SortOption = 'newest' | 'oldest' | 'updated' | 'name-asc' | 'name-desc';

export default function Library() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { role, isLoading: roleLoading } = useUserRole();
  const [groups, setGroups] = useState<LibraryGroup[]>([]);
  const [items, setItems] = useState<LibraryItem[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [newGroupName, setNewGroupName] = useState("");
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<SortOption>('newest');
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [editingGroupId, setEditingGroupId] = useState<string | null>(null);
  const [editingGroupName, setEditingGroupName] = useState("");
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importTitle, setImportTitle] = useState("");
  const [importGroupId, setImportGroupId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const hasAccess = role === 'student' || role === 'paid' || role === 'admin';

  useEffect(() => {
    if (user && hasAccess) {
      fetchGroups();
      fetchItems();
    }
  }, [user, hasAccess]);

  const fetchGroups = async () => {
    const { data, error } = await supabase
      .from('library_groups')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching groups:', error);
      toast.error('Failed to load groups');
    } else {
      setGroups(data || []);
    }
  };

  const fetchItems = async () => {
    const { data, error } = await supabase
      .from('library_items')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching items:', error);
      toast.error('Failed to load library items');
    } else {
      setItems(data || []);
    }
  };

  const createGroup = async () => {
    if (!newGroupName.trim()) {
      toast.error('Please enter a group name');
      return;
    }

    const { error } = await supabase
      .from('library_groups')
      .insert({ name: newGroupName, user_id: user!.id });

    if (error) {
      console.error('Error creating group:', error);
      toast.error('Failed to create group');
    } else {
      toast.success('Group created successfully');
      setNewGroupName("");
      setIsCreatingGroup(false);
      fetchGroups();
    }
  };

  const deleteGroup = async (groupId: string) => {
    const { error } = await supabase
      .from('library_groups')
      .delete()
      .eq('id', groupId);

    if (error) {
      console.error('Error deleting group:', error);
      toast.error('Failed to delete group');
    } else {
      toast.success('Group deleted successfully');
      fetchGroups();
      fetchItems();
    }
  };

  const renameGroup = async (groupId: string) => {
    if (!editingGroupName.trim()) {
      toast.error('Please enter a group name');
      return;
    }

    const { error } = await supabase
      .from('library_groups')
      .update({ name: editingGroupName })
      .eq('id', groupId);

    if (error) {
      console.error('Error renaming group:', error);
      toast.error('Failed to rename group');
    } else {
      toast.success('Group renamed successfully');
      setEditingGroupId(null);
      setEditingGroupName("");
      fetchGroups();
    }
  };

  const archiveGroup = async (groupId: string, archive: boolean) => {
    const { error } = await supabase
      .from('library_groups')
      .update({ archived: archive })
      .eq('id', groupId);

    if (error) {
      console.error('Error archiving group:', error);
      toast.error('Failed to archive group');
    } else {
      toast.success(archive ? 'Group archived' : 'Group restored');
      fetchGroups();
    }
  };

  const deleteItem = async (itemId: string) => {
    const { error } = await supabase
      .from('library_items')
      .delete()
      .eq('id', itemId);

    if (error) {
      console.error('Error deleting item:', error);
      toast.error('Failed to delete item');
    } else {
      toast.success('Item deleted successfully');
      setSelectedItems(prev => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
      fetchItems();
    }
  };

  const archiveItem = async (itemId: string, archive: boolean) => {
    const { error } = await supabase
      .from('library_items')
      .update({ archived: archive })
      .eq('id', itemId);

    if (error) {
      console.error('Error archiving item:', error);
      toast.error('Failed to archive item');
    } else {
      toast.success(archive ? 'Item archived' : 'Item restored');
      fetchItems();
    }
  };

  const moveItemToGroup = async (itemId: string, groupId: string | null) => {
    const { error } = await supabase
      .from('library_items')
      .update({ group_id: groupId })
      .eq('id', itemId);

    if (error) {
      console.error('Error moving item:', error);
      toast.error('Failed to move item');
    } else {
      toast.success('Item moved successfully');
      fetchItems();
    }
  };

  const duplicateItem = async (item: LibraryItem) => {
    const { error } = await supabase
      .from('library_items')
      .insert({
        user_id: user!.id,
        group_id: item.group_id,
        result_type: item.result_type + " (Copy)",
        query: item.query,
        solution: item.solution,
        archived: false
      });

    if (error) {
      console.error('Error duplicating item:', error);
      toast.error('Failed to duplicate item');
    } else {
      toast.success('Item duplicated');
      fetchItems();
    }
  };

  const exportItem = (item: LibraryItem, format: 'latex' | 'text') => {
    let content: string;
    let filename: string;
    let mimeType: string;

    if (format === 'latex') {
      content = `% Query: ${item.query}\n% Generated by CalcLab SuperCalc\n\n${item.solution}`;
      filename = `${item.result_type.replace(/\s+/g, '_')}.tex`;
      mimeType = 'text/x-latex';
    } else {
      content = `Query: ${item.query}\n\nSolution:\n${item.solution}`;
      filename = `${item.result_type.replace(/\s+/g, '_')}.txt`;
      mimeType = 'text/plain';
    }

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported as ${format.toUpperCase()}`);
  };

  // Bulk operations
  const bulkArchive = async (archive: boolean) => {
    const itemIds = Array.from(selectedItems);
    const { error } = await supabase
      .from('library_items')
      .update({ archived: archive })
      .in('id', itemIds);

    if (error) {
      toast.error('Failed to update items');
    } else {
      toast.success(`${itemIds.length} items ${archive ? 'archived' : 'restored'}`);
      setSelectedItems(new Set());
      fetchItems();
    }
  };

  const bulkDelete = async () => {
    const itemIds = Array.from(selectedItems);
    const { error } = await supabase
      .from('library_items')
      .delete()
      .in('id', itemIds);

    if (error) {
      toast.error('Failed to delete items');
    } else {
      toast.success(`${itemIds.length} items deleted`);
      setSelectedItems(new Set());
      fetchItems();
    }
  };

  const bulkMove = async (groupId: string | null) => {
    const itemIds = Array.from(selectedItems);
    const { error } = await supabase
      .from('library_items')
      .update({ group_id: groupId })
      .in('id', itemIds);

    if (error) {
      toast.error('Failed to move items');
    } else {
      toast.success(`${itemIds.length} items moved`);
      setSelectedItems(new Set());
      fetchItems();
    }
  };

  const toggleItemSelection = (itemId: string) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const selectAllVisible = () => {
    const visibleIds = filteredAndSortedItems.map(item => item.id);
    setSelectedItems(new Set(visibleIds));
  };

  const clearSelection = () => {
    setSelectedItems(new Set());
  };

  // Filtering and sorting
  const visibleGroups = useMemo(() => {
    return groups.filter(g => showArchived ? true : !g.archived);
  }, [groups, showArchived]);

  const filteredAndSortedItems = useMemo(() => {
    let result = items.filter(item => {
      // Archive filter
      if (!showArchived && item.archived) return false;
      
      // Group filter
      if (selectedGroup === null) {
        if (item.group_id !== null) return false;
      } else if (selectedGroup === 'all') {
        // Show all items
      } else {
        if (item.group_id !== selectedGroup) return false;
      }
      
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return item.query.toLowerCase().includes(query) || 
               item.solution.toLowerCase().includes(query) ||
               item.result_type.toLowerCase().includes(query);
      }
      
      return true;
    });

    // Sort
    switch (sortBy) {
      case 'oldest':
        result.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        break;
      case 'updated':
        result.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
        break;
      case 'name-asc':
        result.sort((a, b) => a.result_type.localeCompare(b.result_type));
        break;
      case 'name-desc':
        result.sort((a, b) => b.result_type.localeCompare(a.result_type));
        break;
      default: // newest
        result.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }

    return result;
  }, [items, selectedGroup, searchQuery, sortBy, showArchived]);

  // Recently used items (last 5 updated, non-archived)
  const recentlyUsedItems = useMemo(() => {
    return items
      .filter(item => !item.archived)
      .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
      .slice(0, 5);
  }, [items]);

  // Import .tex file handler
  const handleFileImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.tex')) {
      toast.error("Please select a .tex file");
      return;
    }

    try {
      const content = await file.text();
      
      // Parse title from LaTeX if present
      const titleMatch = content.match(/\\title\{([^}]+)\}/);
      const extractedTitle = titleMatch ? titleMatch[1] : file.name.replace('.tex', '');
      
      setImportTitle(extractedTitle);
      setIsImportDialogOpen(true);
      
      // Store content temporarily
      (window as any).__pendingImportContent = content;
    } catch (error) {
      toast.error("Failed to read file");
    }
    
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const confirmImport = async () => {
    const content = (window as any).__pendingImportContent;
    if (!content || !user) {
      toast.error("No content to import");
      return;
    }

    const title = importTitle.trim() || "Imported Document";

    const { error } = await supabase
      .from('library_items')
      .insert({
        user_id: user.id,
        group_id: importGroupId,
        result_type: title,
        query: "Imported from .tex file",
        solution: content
      });

    if (error) {
      toast.error("Failed to import file");
    } else {
      toast.success("File imported successfully!");
      setIsImportDialogOpen(false);
      setImportTitle("");
      setImportGroupId(null);
      delete (window as any).__pendingImportContent;
      fetchItems();
    }
  };

  if (roleLoading) {
    return (
      <PageLayout>
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">{t('library.title')}</h1>
            <p className="text-muted-foreground">Loading...</p>
          </div>
          <div className="flex justify-center items-center min-h-[400px]">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!user) {
    return (
      <PageLayout>
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">{t('library.title')}</h1>
            <p className="text-muted-foreground">Save and organize your calculation results</p>
          </div>
          <Card className="max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                Sign In Required
              </CardTitle>
              <CardDescription>
                Please sign in to access your library
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button asChild className="w-full">
                <Link to="/auth">Sign In</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </PageLayout>
    );
  }

  if (!hasAccess) {
    return (
      <PageLayout>
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2">{t('library.title')}</h1>
            <p className="text-muted-foreground">Save and organize your calculation results</p>
          </div>
          <Card className="max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="h-5 w-5" />
                {t('library.premiumFeature')}
              </CardTitle>
              <CardDescription>
                The Library is available for Student and Paid members
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                {t('library.upgradeMessage')}
              </p>
              <Button asChild className="w-full">
                <Link to="/membership">View Membership Plans</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">{t('library.title')}</h1>
          <p className="text-muted-foreground">Save and organize your SuperCalc results into groups for lesson planning</p>
        </div>
        
        {/* SuperCalc CTA */}
        <Card className="mb-6 bg-gradient-to-br from-supercalc-brand/10 to-supercalc-brand/5 border-supercalc-brand/30">
          <CardContent className="p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Brain className="h-8 w-8 text-supercalc-brand" />
              <div>
                <p className="font-semibold">Create content with SuperCalc AI</p>
                <p className="text-sm text-muted-foreground">Generate lesson plans, solve problems, then save here</p>
              </div>
            </div>
            <Button asChild className="bg-supercalc-brand hover:bg-supercalc-brand/90">
              <Link to="/supercalc">
                <Sparkles className="h-4 w-4 mr-2" />
                Open SuperCalc
              </Link>
            </Button>
          </CardContent>
        </Card>

        {/* Import and Recently Used Row */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Import Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Upload className="h-5 w-5" />
                Import LaTeX File
              </CardTitle>
              <CardDescription>Upload a .tex file to add to your library</CardDescription>
            </CardHeader>
            <CardContent>
              <input
                ref={fileInputRef}
                type="file"
                accept=".tex"
                onChange={handleFileImport}
                className="hidden"
              />
              <Button 
                variant="outline" 
                onClick={() => fileInputRef.current?.click()}
                className="w-full"
              >
                <Upload className="h-4 w-4 mr-2" />
                Choose .tex File
              </Button>
            </CardContent>
          </Card>

          {/* Recently Used */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Recently Used
              </CardTitle>
              <CardDescription>Quick access to your latest items</CardDescription>
            </CardHeader>
            <CardContent>
              {recentlyUsedItems.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No recent items</p>
              ) : (
                <div className="space-y-2">
                  {recentlyUsedItems.map(item => (
                    <div 
                      key={item.id} 
                      className="flex items-center justify-between p-2 rounded-lg bg-muted/50 hover:bg-muted transition-colors cursor-pointer"
                      onClick={() => {
                        setSelectedGroup(item.group_id || null);
                        setSearchQuery(item.result_type);
                      }}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{item.result_type}</p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(item.updated_at).toLocaleDateString()}
                        </p>
                      </div>
                      <FileText className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Import Dialog */}
        <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Import LaTeX File</DialogTitle>
              <DialogDescription>
                Confirm the title and select a group for your imported file
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={importTitle}
                  onChange={(e) => setImportTitle(e.target.value)}
                  placeholder="Enter title..."
                />
              </div>
              <div>
                <Label>Group (optional)</Label>
                <Select value={importGroupId || ""} onValueChange={(v) => setImportGroupId(v || null)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a group..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Ungrouped</SelectItem>
                    {groups.filter(g => !g.archived).map(g => (
                      <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={confirmImport} className="w-full">
                Import to Library
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <div className="space-y-6">
          {/* Search and Sort Bar */}
          <Card>
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search queries, solutions, or titles..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <div className="flex gap-2 items-center">
                  <Select value={sortBy} onValueChange={(v) => setSortBy(v as SortOption)}>
                    <SelectTrigger className="w-[160px]">
                      <ArrowUpDown className="h-4 w-4 mr-2" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="newest">Newest First</SelectItem>
                      <SelectItem value="oldest">Oldest First</SelectItem>
                      <SelectItem value="updated">Recently Updated</SelectItem>
                      <SelectItem value="name-asc">Name A-Z</SelectItem>
                      <SelectItem value="name-desc">Name Z-A</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant={showArchived ? "default" : "outline"}
                    size="sm"
                    onClick={() => setShowArchived(!showArchived)}
                  >
                    <Archive className="h-4 w-4 mr-2" />
                    {showArchived ? "Hide" : "Show"} Archived
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Groups Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Folder className="h-5 w-5" />
                    Groups
                  </CardTitle>
                  <CardDescription>Organize your saved results into groups</CardDescription>
                </div>
                <Dialog open={isCreatingGroup} onOpenChange={setIsCreatingGroup}>
                  <DialogTrigger asChild>
                    <Button size="sm" className="gap-2">
                      <Plus className="h-4 w-4" />
                      New Group
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Create New Group</DialogTitle>
                      <DialogDescription>
                        Give your group a name to organize your results (e.g., "Physics Unit 3", "Algebra Lessons")
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <Input
                        placeholder="Group name"
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && createGroup()}
                      />
                      <Button onClick={createGroup} className="w-full">
                        Create Group
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant={selectedGroup === 'all' ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedGroup('all')}
                >
                  All Items ({items.filter(i => showArchived || !i.archived).length})
                </Button>
                <Button
                  variant={selectedGroup === null ? "default" : "outline"}
                  size="sm"
                  onClick={() => setSelectedGroup(null)}
                >
                  Ungrouped ({items.filter(i => !i.group_id && (showArchived || !i.archived)).length})
                </Button>
                {visibleGroups.map((group) => (
                  <div key={group.id} className="flex items-center gap-1">
                    {editingGroupId === group.id ? (
                      <div className="flex items-center gap-1">
                        <Input
                          value={editingGroupName}
                          onChange={(e) => setEditingGroupName(e.target.value)}
                          className="h-8 w-32"
                          onKeyDown={(e) => e.key === 'Enter' && renameGroup(group.id)}
                        />
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => renameGroup(group.id)}>
                          <Check className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <>
                        <Button
                          variant={selectedGroup === group.id ? "default" : "outline"}
                          size="sm"
                          onClick={() => setSelectedGroup(group.id)}
                          className={group.archived ? "opacity-60" : ""}
                        >
                          {group.name} ({items.filter(i => i.group_id === group.id && (showArchived || !i.archived)).length})
                          {group.archived && <Archive className="h-3 w-3 ml-1" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => { setEditingGroupId(group.id); setEditingGroupName(group.name); }}
                        >
                          <Edit2 className="h-3 w-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => archiveGroup(group.id, !group.archived)}
                        >
                          {group.archived ? <ArchiveRestore className="h-3 w-3" /> : <Archive className="h-3 w-3" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => deleteGroup(group.id)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Bulk Actions */}
          {selectedItems.size > 0 && (
            <Card className="border-primary">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-medium">{selectedItems.size} selected</span>
                  <Button variant="outline" size="sm" onClick={clearSelection}>Clear</Button>
                  <Button variant="outline" size="sm" onClick={() => bulkArchive(true)}>
                    <Archive className="h-4 w-4 mr-2" /> Archive
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => bulkArchive(false)}>
                    <ArchiveRestore className="h-4 w-4 mr-2" /> Restore
                  </Button>
                  <Select onValueChange={(v) => bulkMove(v === 'ungrouped' ? null : v)}>
                    <SelectTrigger className="w-[150px]">
                      <SelectValue placeholder="Move to..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ungrouped">Ungrouped</SelectItem>
                      {groups.filter(g => !g.archived).map((group) => (
                        <SelectItem key={group.id} value={group.id}>{group.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="destructive" size="sm" onClick={bulkDelete}>
                    <Trash2 className="h-4 w-4 mr-2" /> Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Items Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Saved Results ({filteredAndSortedItems.length})
                  </CardTitle>
                  <CardDescription>
                    {selectedGroup === 'all' 
                      ? "All saved results" 
                      : selectedGroup 
                        ? `Results in ${groups.find(g => g.id === selectedGroup)?.name}`
                        : "Ungrouped results"}
                  </CardDescription>
                </div>
                {filteredAndSortedItems.length > 0 && (
                  <Button variant="outline" size="sm" onClick={selectAllVisible}>
                    Select All
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {filteredAndSortedItems.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground mb-4">
                    {searchQuery ? "No results match your search" : "No saved results yet"}
                  </p>
                  {!searchQuery && (
                    <Button asChild>
                      <Link to="/supercalc">
                        <Brain className="h-4 w-4 mr-2" />
                        Create with SuperCalc
                      </Link>
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredAndSortedItems.map((item) => (
                    <Card key={item.id} className={item.archived ? "opacity-60" : ""}>
                      <CardHeader>
                        <div className="flex items-start gap-3">
                          <Checkbox
                            checked={selectedItems.has(item.id)}
                            onCheckedChange={() => toggleItemSelection(item.id)}
                          />
                          <div className="flex-1">
                            <div className="flex items-start justify-between">
                              <div>
                                <CardTitle className="text-base flex items-center gap-2">
                                  {item.result_type}
                                  {item.archived && <Archive className="h-4 w-4 text-muted-foreground" />}
                                </CardTitle>
                                <CardDescription className="mt-1">
                                  Created: {new Date(item.created_at).toLocaleDateString()} • 
                                  Updated: {new Date(item.updated_at).toLocaleDateString()}
                                </CardDescription>
                              </div>
                              <div className="flex items-center gap-2">
                                <Select
                                  value={item.group_id || "ungrouped"}
                                  onValueChange={(value) => 
                                    moveItemToGroup(item.id, value === "ungrouped" ? null : value)
                                  }
                                >
                                  <SelectTrigger className="w-[140px]">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="ungrouped">Ungrouped</SelectItem>
                                    {groups.filter(g => !g.archived).map((group) => (
                                      <SelectItem key={group.id} value={group.id}>
                                        {group.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                                <Button variant="ghost" size="icon" onClick={() => duplicateItem(item)} title="Duplicate">
                                  <Copy className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => exportItem(item, 'latex')} title="Export LaTeX">
                                  <Download className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => archiveItem(item.id, !item.archived)} title={item.archived ? "Restore" : "Archive"}>
                                  {item.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                                </Button>
                                <Button variant="ghost" size="icon" onClick={() => deleteItem(item.id)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4 pl-10">
                        <div>
                          <h4 className="font-semibold mb-2 text-sm">Query:</h4>
                          <p className="text-sm text-muted-foreground">{item.query}</p>
                        </div>
                        <div>
                          <h4 className="font-semibold mb-2 text-sm">Solution:</h4>
                          <div className="prose dark:prose-invert max-w-none text-sm">
                            <Math>{item.solution}</Math>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* LaTeX Document Editor CTA */}
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="p-6 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="font-semibold text-lg mb-1">Need to create custom LaTeX documents?</h3>
                <p className="text-muted-foreground">Use our LaTeX Document Editor to create lesson plans, reports, and assessments from scratch.</p>
              </div>
              <Button asChild>
                <Link to="/tools/latex-editor">
                  Open LaTeX Editor
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </PageLayout>
  );
}